import React from 'react';
import Button from '../components/UI/Button';
import { getProductById } from '../data/products';
import './WoprPage.css';
import './ProductHero.css';
import idleveilCircleLogo from '../assets/projects/idleveil-circlelogo.webp';

const IdleveilPage = () => {
  const product = getProductById('idleveil');

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="wopr-page">
      <section className="product-hero product-hero-idleveil">
        <div className="product-hero-content">
          <div className="product-hero-logo">
            <span className="idleveil-wordmark" aria-label="IdleVeil">
              <span className="iv-diamond" aria-hidden="true">&#9670;</span>
              <span className="iv-name">IdleVeil</span>
            </span>
          </div>
          <p className="product-hero-tagline">Forge Your Legend. Conquer the Arena.</p>
        </div>
      </section>

      <section className="product-overview-section">
        <div className="container">
          <div className="product-overview">
            <div className="product-overview-image">
              <img src={product.image} alt={product.name} />
            </div>
            <div className="product-overview-content">
              <h2 className="section-title">About {product.name}</h2>
              <p className="product-overview-desc">{product.description}</p>
              <p className="product-overview-desc">
                Every decision matters in this hardcore RPG. Build your character,
                collect loot, and fight your way through challenging encounters.
                But be careful—death is permanent. When your character falls,
                they're gone forever, though your account progress and achievements remain.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section bg-light">
        <div className="container">
          <h2 className="section-title text-center">Features</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem' }}>
            A strategic RPG where every choice counts.
          </p>
          <div className="features-grid">
            {product.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <span className="feature-check">✓</span>
                <span className="feature-text">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="product-detail-section">
        <div className="container">
          <h2 className="section-title text-center">How It Works</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem', maxWidth: '700px' }}>
            Create a hero, battle monsters, and climb the ranks — but death is permanent. Every decision matters.
          </p>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>Choose Your Class</h3>
              <p>Pick from five distinct classes:</p>
              <ul className="detail-list">
                <li><strong>Warrior</strong> — High attack and defense</li>
                <li><strong>Mage</strong> — Devastating magic power</li>
                <li><strong>Ranger</strong> — Balanced with high agility</li>
                <li><strong>Rogue</strong> — Fast precision strikes</li>
                <li><strong>Paladin</strong> — Resilient hybrid fighter</li>
              </ul>
            </div>
            <div className="detail-card">
              <h3>Combat System</h3>
              <p>
                Turn-based auto-combat with rich narrative text describing every attack, dodge, and critical hit.
                Strategic gear choices and stat allocation determine your effectiveness in battle.
              </p>
            </div>
            <div className="detail-card">
              <h3>Progression</h3>
              <p>
                Level up by defeating 40+ enemy types across 25 levels. Earn gold for better equipment and
                allocate stat points to customize your build.
              </p>
            </div>
            <div className="detail-card">
              <h3>Social Features</h3>
              <p>
                Join a guild for XP bonuses and raid content. Complete daily missions, challenge other players
                in PvP, and climb the leaderboards.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="product-detail-section bg-light">
        <div className="container">
          <h2 className="section-title text-center">The Permadeath System</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem', maxWidth: '600px' }}>
            Every battle carries real risk. Every victory feels earned.
          </p>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>What's Lost on Death</h3>
              <ul className="detail-list">
                <li>Your character, level, stats, and equipped gear</li>
                <li>All inventory items and gold</li>
                <li>Mission progress and reputation</li>
              </ul>
            </div>
            <div className="detail-card">
              <h3>What Persists Forever</h3>
              <ul className="detail-list">
                <li><strong>Account level and XP</strong></li>
                <li><strong>Silver currency</strong> (premium)</li>
                <li><strong>Achievements and titles</strong></li>
                <li><strong>Friends and guild membership</strong></li>
                <li><strong>Bloodline bonuses</strong> from fallen heroes</li>
              </ul>
            </div>
            <div className="detail-card detail-card-wide">
              <h3>The Hall of Legends</h3>
              <p>
                Fallen heroes are immortalized in the Hall of Legends — a graveyard where players can browse
                the greatest warriors who met their end. Your character's legacy lives on even after death,
                and each fallen hero adds to your bloodline, giving future characters small stat bonuses.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="product-detail-section">
        <div className="container">
          <h2 className="section-title text-center">Getting Started</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem', maxWidth: '600px' }}>
            Jump into the action in minutes.
          </p>
          <div className="steps-grid">
            <div className="step-item">
              <span className="step-number">1</span>
              <div className="step-content">
                <h4>Sign In with Google</h4>
                <p>Create your account and choose a unique username.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <div className="step-content">
                <h4>Create Your Character</h4>
                <p>Pick a class, select your portrait, and name your hero.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">3</span>
              <div className="step-content">
                <h4>Fight Your First Battle</h4>
                <p>Challenge a monster and watch the combat unfold.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">4</span>
              <div className="step-content">
                <h4>Gear Up & Level Up</h4>
                <p>Buy equipment and allocate stat points as you grow.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">5</span>
              <div className="step-content">
                <h4>Join a Guild</h4>
                <p>Get XP bonuses, access raids, and make friends.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">6</span>
              <div className="step-content">
                <h4>Challenge Others</h4>
                <p>Test your build in PvP battles for glory and rewards.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="get-started-section">
        <div className="container">
          <div className="get-started-grid">
            <div className="get-started-download">
              <div className="get-started-title-row">
                <h2 className="section-title">Play {product.name}</h2>
                <img src={idleveilCircleLogo} alt="Idleveil" className="get-started-logo" />
              </div>
              <p className="get-started-subtitle">Live and in alpha. Free to play in your browser.</p>
              <div className="download-buttons">
                <Button href={product.playUrl} variant="primary" size="large">
                  Play Now
                </Button>
              </div>
              <div className="requirements-inline">
                <span className="requirements-label">Requirements:</span>
                {product.requirements.map((req, index) => (
                  <span key={index} className="requirement-tag">{req}</span>
                ))}
              </div>
            </div>
            <div className="get-started-support">
              <h2 className="section-title">Need Help?</h2>
              <p className="get-started-subtitle">
                If you have questions or need support, we're here to help.
              </p>
              <Button to="/contact" variant="outline-dark">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IdleveilPage;
